import React, { useState } from 'react';
import { LogOut, User, Clock } from 'lucide-react';
import styles from './logout-popup.module.css';
import { useSelector } from 'react-redux';
import { getTimeDiff } from '../../utils/utils';
import { unregisterFCMToken } from '../../../app/config/firebase/pushNotifications';
import { useRememberMe } from '../../hooks/useRememberMe';
import Modal from '../modal/Modal';

export const LogoutPopup = ({ isOpen, onClose, onConfirm, userInfo }) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { loginTime } = useSelector((e) => e?.user);
  const { clearSession } = useRememberMe();
  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      await unregisterFCMToken(); // needs the auth token, so run before logout
    } finally {
      setIsLoggingOut(false);
      onConfirm();
      localStorage.removeItem('token');
      localStorage.removeItem('user_id');
      clearSession();
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      show
      title="Confirm Logout"
      onClose={onClose}
      showCloseButton={false}
      showCrossButton={!isLoggingOut}
      size="sm"
      isLock={false}
      buttons={[
        { label: 'Cancel', onClick: onClose, disabled: isLoggingOut, variant: 'secondary' },
        {
          label: isLoggingOut ? (
            <>
              <span className={styles.spinner} />
              Logging out...
            </>
          ) : (
            <>
              <LogOut size={16} />
              Logout
            </>
          ),
          onClick: handleConfirmLogout,
          disabled: isLoggingOut,
          variant: 'danger',
        },
      ]}
    >
        <div className={styles.content}>
          {/* User Info */}
          <div className={styles.userInfo}>
            <div className={styles.userAvatar}>
              <User size={32} />
            </div>
            <div className={styles.userDetails}>
              <h4 className={styles.userName}>
                {userInfo?.name || 'Admin User'}
              </h4>
              <p className={styles.userEmail}>
                {userInfo?.email || 'admin@school.com'}
              </p>
            </div>
          </div>

          {/* Warning Message */}
          <div className={styles.warningMessage}>
            <p>Are you sure you want to logout from your account?</p>
            <div className={styles.warningDetails}>
              <div className={styles.warningItem}>
                <Clock size={16} />
                <span>Your session will be terminated</span>
              </div>
              <div className={styles.warningItem}>
                <LogOut size={16} />
                <span>You'll need to login again to access your account</span>
              </div>
            </div>
          </div>

          {/* Session Info */}
          <div className={styles.sessionInfo}>
            <div className={styles.sessionItem}>
              <span className={styles.sessionLabel}>Login Time:</span>
              <span className={styles.sessionValue}>
                {(new Date(loginTime)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <div className={styles.sessionItem}>
              <span className={styles.sessionLabel}>Session Duration:</span>
              <span className={styles.sessionValue}>{getTimeDiff(loginTime)}</span>
            </div>
          </div>
        </div>
    </Modal>
  );
}